import { useCallback, useState } from 'react';
import type { Vehicle, VehicleDerivative } from '@/features/vehiclesearch';
import { useAppDispatch } from '@/app/store/hooks';
import { setSelectedVehicle } from '../store/quoteSlice';
import {
    applyVehicleIdentity,
    resolveVehicleIdentityGate,
    type VehicleIdentity,
    type VehicleIdentityGateState,
} from '../utils/vehicleIdentity';

export type TerminalIntent = 'complete' | 'checkout';

export interface VehicleIdentityGate {
    state: VehicleIdentityGateState;
    isOpen: boolean;
    isSaving: boolean;
    pendingIntent: TerminalIntent | null;
    resolvedIntent: TerminalIntent | null;
    /** true => the caller may run the terminal action now. false => the gate has taken over. */
    allow: (intent: TerminalIntent) => boolean;
    submit: (identity: VehicleIdentity) => Promise<void>;
    cancel: () => void;
    clearResolvedIntent: () => void;
}

// The flush runs through the quote's save lane, which surfaces its own failures.
export function useVehicleIdentityGate(params: {
    vehicle: Vehicle | VehicleDerivative | null;
    flushSave: () => Promise<unknown>;
}): VehicleIdentityGate {
    const { vehicle, flushSave } = params;
    const dispatch = useAppDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingIntent, setPendingIntent] = useState<TerminalIntent | null>(null);
    const [resolvedIntent, setResolvedIntent] = useState<TerminalIntent | null>(null);
    const [latchedVehicle, setLatchedVehicle] = useState<Vehicle | null>(null);

    const state = resolveVehicleIdentityGate(vehicle);

    // Dispatched into Redux but never persisted, so it must keep the gate closed however
    // satisfied the slice looks — but only while the slice still holds that same document.
    const unpersistedVehicle =
        latchedVehicle !== null && vehicle === latchedVehicle ? latchedVehicle : null;

    const allow = useCallback(
        (intent: TerminalIntent): boolean => {
            const gate = resolveVehicleIdentityGate(vehicle);
            if (gate.kind === 'satisfied' && unpersistedVehicle === null) return true;
            // unrepairable refuses rather than improvising — no mop-up opens, because
            // there is nothing the modal could repair.
            if (gate.kind !== 'unrepairable') {
                setPendingIntent(intent);
                setIsOpen(true);
            }
            return false;
        },
        [vehicle, unpersistedVehicle],
    );

    const submit = useCallback(
        async (identity: VehicleIdentity): Promise<void> => {
            const gate = resolveVehicleIdentityGate(vehicle);
            // Never a remembered document: unpersistedVehicle is non-null only when it IS the
            // slice's current vehicle, so a retry can never save an abandoned journey's vehicle.
            const retryBase = gate.kind === 'satisfied' ? unpersistedVehicle : null;
            const base = gate.kind === 'missing' ? gate.vehicle : retryBase;
            // Clear pendingIntent on the early return too: it sits outside the try/finally,
            // and a stranded pendingIntent means a user's press is silently dropped.
            if (base === null) {
                setIsOpen(false);
                setPendingIntent(null);
                return;
            }

            const updated = applyVehicleIdentity(base, identity);
            setIsSaving(true);
            // Dispatch FIRST: the lane reads the vehicle from the slice when its run starts.
            dispatch(setSelectedVehicle(updated));
            try {
                await flushSave();
                setLatchedVehicle(null);
                setIsOpen(false);
                setResolvedIntent(pendingIntent);
            } catch {
                // Not a silent swallow: the lane already surfaced the failure.
                // This catch only suppresses the continuation.
                setLatchedVehicle(updated);
                setIsOpen(false);
            } finally {
                setIsSaving(false);
                setPendingIntent(null);
            }
        },
        [vehicle, flushSave, dispatch, pendingIntent, unpersistedVehicle],
    );

    const cancel = useCallback(() => {
        setIsOpen(false);
        setPendingIntent(null);
    }, []);

    const clearResolvedIntent = useCallback(() => setResolvedIntent(null), []);

    return {
        state,
        isOpen,
        isSaving,
        pendingIntent,
        resolvedIntent,
        allow,
        submit,
        cancel,
        clearResolvedIntent,
    };
}
